import React, { useEffect, useRef, useState } from "react";
import { useForm, Controller, set } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Grid,
  MenuItem,
  TextField,
  Typography,
  Paper,
  Table,
  TableCell,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import BusinessIcon from "@mui/icons-material/Business";
import SchoolIcon from "@mui/icons-material/School";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import api from "../../api/client";
import { useLocation, useNavigate } from "react-router-dom";
import { useSnackbar } from "../../providers/SnackbarProvider";
import { OverAllLoader } from "../../components/OverAllLoader";
import GroupIcon from "@mui/icons-material/Group";
import { useChangeableItems, getLabelByKey } from "../../store/useDynamicLabel";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import { BrainCircuitIcon } from "lucide-react";

const proficiencyOptions = [
  {
    proficiency_id: 1,
    proficiency_name: "Beginner",
  },
  {
    proficiency_id: 2,
    proficiency_name: "Intermediate",
  },
  {
    proficiency_id: 3,
    proficiency_name: "Advanced",
  },
  {
    proficiency_id: 4,
    proficiency_name: "Expert",
  },
];

const AddStudent = () => {

  const changeableItems = useChangeableItems();
  const getLabel = (itemKey) => getLabelByKey(changeableItems, itemKey);

  const [loading, setLoading] = useState(false);
  const [instituteNames, setInstituteNames] = useState([]);
  const [batchOptions, setBatchOptions] = useState([]);
  const location = useLocation();
  const statuses = ["ACTIVE", "INACTIVE"];
  const [overAllLoading, setOverAllLoading] = useState(false);
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const firstText = useRef();
  const userRole = localStorage.getItem("role");
  const organizationId = localStorage.getItem("organization_id")
  const [trainerNames, setTrainerNames] = useState([]);
  const [trainerIds, setTrainerIds] = useState([]);
  const [resumeFile, setResumeFile] = useState(null);
  const [skills, setSkills] = useState([]);
  const [resumeURL, setResumeURL] = useState("")

  const userData = JSON.parse(localStorage.getItem("userData"));
  const userId = userData?.user_id;
  const { addStudent, StudentId } = location.state || "";

  // ===== validation ========
  const schema = Yup.object().shape({
    firstName: Yup.string()
    .required("Please enter the first name")
    .trim()
      .matches(/^[A-Za-z]+(?:[ .'-]+[A-Za-z]+)*\.?$/,"First name can only contain letters")
      .min(2, "First name must be at least 2 characters")
      .max(50, "First name must not exceed 50 characters"),
    lastName: Yup.string()
      .required("Please enter the last name")
      .matches(/^[A-Za-z]+(?:[ '-][A-Za-z]+)*$/,"Last name can only contain letters")
      .min(2, "Last name must be at least 2 characters")
      .max(50, "Last name must not exceed 50 characters"),
    gender: Yup.string().required("Please select the gender"),
    dob: Yup.date()
      .required("Please enter the date of birth")
      .max(new Date(), "DOB cannot be in the future")
      .test(
        "min-age",
        `${getLabel("STUDENT")} must be at least 12 years old`,
        (value) => !value || !dayjs(value).isAfter(dayjs().subtract(12, "year"), "day")
      )
      .test(
        "max-age",
        `${getLabel("STUDENT")} age cannot exceed 100 years`,
        (value) => !value || !dayjs(value).isBefore(dayjs().subtract(100, "year"), "day")
      )
      .typeError("Please enter a valid date"),
    contactNumber: Yup.string()
      .required("Please enter the contact number")
      .min(10, "Atleast 10 numbers is required")
      .max(15, "Contact number must not exceed 15 digits"),
    email: Yup.string()
      .required("Please enter your Email Address")
      .matches(
        /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
        "Please enter a valid Email Address")
      .min(5, "Email must be at least 5 characters")
      .max(100, "Email must not exceed 100 characters"),
    instituteName: Yup.mixed().required("Please enter the institute name"),
    assignTrainer:
      userRole === "INSTITUTE_ADMIN"
        ? Yup.array()
          .of(Yup.number())
          .min(1, "Please assign at least one trainer")
          .required("Assigning a trainer is required")
        : Yup.array().notRequired(),
    batch: Yup.string().required("Please select a batch"),

    address: Yup.string()
      .notRequired()
      .trim(),

    city: Yup.string()
      .notRequired()
      .trim(),

    state: Yup.string()
      .notRequired()
      .trim(),

    pincode: Yup.string()
      .notRequired("Please enter the pincode")
      .trim(),

    totalExperience:
      organizationId === "2" ?
        Yup.number()
          .typeError("Please enter total experience")
          .required("Please enter total experience")
          .min(0, "Experience cannot be negative")
        :
        Yup.number().notRequired(),
    designation:
      organizationId === "2" ?
        Yup.string()
          .required("Please enter the designation")
          .trim()
        :
        Yup.string().notRequired(),

    skills:
      organizationId === "2" ?
        Yup.array()
          .min(1, "Please select at least one skill")
          .required("Please select at least one skill")
          .of(
            Yup.object().shape({
              skill_id: Yup.number()
                .required("Skill is required"),

              proficiency_id: Yup.number()
                .nullable()
                .typeError("Please select proficiency")
                .required("Please select proficiency"),

              relevant_experience: Yup.number()
                .nullable()
                .typeError("Please enter relevant experience")
                .required("Please enter relevant experience")
                .min(0, "Experience cannot be negative"),
            })
          )
        :
        Yup.array().notRequired(),
  });

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      firstName: "",
      lastName: "",
      gender: "Male",
      dob: "",
      contactNumber: "",
      email: "",
      address: "",
      city: "",
      state: "",
      pincode: "",
      status: "ACTIVE",
      instituteName: "",
      batch: "",
      assignTrainer: [],
      designation: "",
      skills: [],
      totalExperience: "",
      ugQualification: "",
      pgQualification: ""
    },
  });

  const initialize = async () => {
    setOverAllLoading(true);
    try {
      // 1. Fetch dropdown options in parallel using settled promises for high reliability
      const [instRes, trainerRes, batchRes] = await Promise.allSettled([
        api.post("ListInstituteNames"),
        api.post("ListTrainersForDropdown"),
        api.post("getAllBatchList"),
      ]);

      const institutes = instRes.status === "fulfilled" ? (instRes.value?.data?.data ?? []) : [];
      const trainers = trainerRes.status === "fulfilled" ? (trainerRes.value?.data?.data ?? []) : [];
      const batches = batchRes.status === "fulfilled" ? (batchRes.value?.data?.data ?? []) : [];

      setInstituteNames(institutes);
      setTrainerNames(trainers);
      setBatchOptions(batches);

      // Auto-select trainers where is_selectable === false
      const autoSelected = trainers
        .filter((t) => t.is_selectable === false)
        .map((t) => t.trainer_id);

      // Determine initial/default institute (Use actual logged-in user's institute if available)
      const loggedInInstituteId = +localStorage.getItem("InstituteId") || "";
      let defaultInstituteId = "";
      if (userRole === "INSTITUTE_ADMIN" || userRole === "TRAINER") {
        defaultInstituteId = loggedInInstituteId || (institutes[0]?.institute_id ?? "");
      }

      // 2. Fetch student by ID if editing, otherwise reset to create defaults
      if (StudentId !== null && StudentId !== undefined) {
        const res = await api.post("ViewStudentByID", {
          user_id: StudentId,
        });

        if (res?.data?.status === "success") {
          const studentData = res.data.data;
          const matchedTrainerIds = studentData.assigned_trainers?.map((t) => t.trainer_user_id) || [];
          setTrainerIds(matchedTrainerIds);

          reset(
            organizationId === "1" ? {
              firstName: studentData.first_name,
              lastName: studentData.last_name,
              contactNumber: studentData.phone,
              email: studentData.email,
              instituteName: studentData.institute_id,
              status: studentData.status,
              address: studentData.address || "",
              city: studentData.city || "",
              state: studentData.state || "",
              pincode: studentData.pincode || "",
              gender: studentData.gender,
              dob: studentData.dob,
              assignTrainer: matchedTrainerIds,
              batch: studentData.batch_id || "",
            } : {
              firstName: studentData?.first_name,
              lastName: studentData?.last_name,
              contactNumber: studentData?.phone,
              email: studentData?.email,
              status: studentData?.status,
              address: studentData?.address || "",
              city: studentData?.city || "",
              state: studentData?.state || "",
              pincode: studentData?.pincode || "",
              gender: studentData?.gender,
              instituteName: studentData.institute_id,
              dob: studentData?.dob,
              skills: studentData?.candidate_profile?.skills?.map((skill) => {
                const proficiency = proficiencyOptions?.find((item) => item?.proficiency_name === skill?.proficiency);

                return {
                  skill_id: skill?.skill_id,
                  proficiency_id: proficiency?.proficiency_id || null,
                  relevant_experience: skill?.years_of_experience || null,
                }
              }),
              totalExperience: studentData?.candidate_profile?.total_experience_years || "",
              designation: studentData?.candidate_profile?.designation || "",
              department: studentData?.candidate_profile?.department || "",
              address: studentData?.address || "",
              city: studentData?.city || "",
              state: studentData?.state || "",
              pincode: studentData?.pincode || "",
              ugQualification: studentData?.candidate_profile?.ug_qualification || "",
              pgQualification: studentData?.candidate_profile?.pg_qualification || ""
            });
          if (organizationId === "2") {
            setResumeFile(studentData?.candidate_profile?.resume_url)
          }
        } else {
          showSnackbar("error", res?.data?.message || "Failed to load student details");
        }
      } else {
        // Create Mode
        const initialTrainerVal = userRole === "TRAINER" ? [] : autoSelected;
        setTrainerIds(initialTrainerVal);

        reset({
          firstName: "",
          lastName: "",
          gender: "Male",
          dob: "",
          contactNumber: "",
          email: "",
          address: "",
          city: "",
          state: "",
          pincode: "",
          status: "ACTIVE",
          instituteName: defaultInstituteId,
          batch: "",
          assignTrainer: initialTrainerVal,
        });
      }

      // Focus on first element
      if (firstText.current) {
        firstText.current.focus();
      }
    } catch (error) {
      console.error("Initialization error:", error);
      showSnackbar("error", "An error occurred while loading form data");
    } finally {
      setOverAllLoading(false);
    }
  };

  useEffect(() => {
    initialize();
  }, [StudentId]);

  const getSkills = async () => {
    try {
      const response = await api.get('Skills')
      setSkills(response?.data?.data || [])
    } catch (err) {
      console.error("Error fetching skills:", err);
    }
  }

  useEffect(() => {
    getSkills()
  }, [])

  const handleResumeUpload = async (file) => {
    const formData = new FormData();

    formData.append("resume", file);

    try {
      const response = await api.post(
        "UploadCandidateResume",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response?.data?.status === "success") {
        showSnackbar("success", response?.data?.message);
        setResumeURL(response?.data?.data?.resume_url || "")
      } else {
        showSnackbar("error", "Unable to upload the resume");
      }
    } catch (error) {
      console.error("Resume upload failed:", error);
      showSnackbar("error", "Failed to upload the resume");
      setResumeFile(null)
    }
  };

  const onSubmit = async (data) => {
    if (!resumeFile && organizationId === "2") {
      showSnackbar("error", "Please upload your resume")
      return;
    }
    setLoading(true);
    try {
      const activeTrainers = userRole === "TRAINER"
        ? Array.from(new Set([...(trainerIds || []), userId]))
        : trainerIds && trainerIds.length > 0
          ? trainerIds
          : [userId];

      const addCandidateInstitutePayload = {
        first_name: data?.firstName,
        last_name: data?.lastName,
        gender: data?.gender,
        dob: data?.dob ? dayjs(data.dob).format("YYYY-MM-DD") : null,
        phone: data?.contactNumber,
        email: data?.email,
        address: data?.address || "",
        city: data?.city || "",
        state: data?.state || "",
        pincode: data?.pincode || null,
        status: "ACTIVE",
        institute_id: +data?.instituteName,
        batch_id: +data?.batch,
        trainers: activeTrainers,
      };

      const addCandidateCorporatePayload = {
        first_name: data?.firstName,
        last_name: data?.lastName,
        email: data?.email,
        phone: data?.contactNumber,
        institute_id: +data?.instituteName,
        gender: data?.gender,
        dob: data?.dob ? dayjs(data.dob).format("YYYY-MM-DD") : null,
        address: data?.address || "",
        city: data?.city || "",
        state: data?.state || "",
        pincode: data?.pincode || null,
        total_experience_years: data?.totalExperience,
        resume_url: resumeURL,
        designation: data?.designation,
        batch_id: +data?.batch,
        ug_qualification: data?.ugQualification,
        pg_qualification: data?.pgQualification,
        skills: data?.skills?.map((skill) => {
          const proficiency = proficiencyOptions.find(
            (option) =>
              option.proficiency_id === skill.proficiency_id
          );

          return {
            skill_id: skill.skill_id,
            proficiency: proficiency?.proficiency_name || "",
            years_of_experience: skill.relevant_experience,
          };
        }),
      }

      const createPayload = organizationId === "2" ? addCandidateCorporatePayload : addCandidateInstitutePayload

      const updateCandidateInstitutePayload = {
        user_id: StudentId,
        first_name: data?.firstName,
        last_name: data?.lastName,
        gender: data?.gender,
        dob: data?.dob ? dayjs(data.dob).format("YYYY-MM-DD") : null,
        phone: data?.contactNumber,
        email: data?.email,
        address: data?.address || "",
        city: data?.city || "",
        state: data?.state || "",
        pincode: data?.pincode || null,
        status: data?.status,
        institute_id: +data?.instituteName,
        batch_id: +data?.batch,
        trainers: activeTrainers,
      };

      const updateCandidateCorporatePayload = {
        user_id: StudentId,
        first_name: data?.firstName,
        last_name: data?.lastName,
        email: data?.email,
        phone: data?.contactNumber,
        institute_id: +data?.instituteName,
        gender: data?.gender,
        dob: data?.dob ? dayjs(data.dob).format("YYYY-MM-DD") : null,
        address: data?.address || "",
        city: data?.city || "",
        state: data?.state || "",
        pincode: data?.pincode || null,
        total_experience_years: data?.totalExperience,
        resume_url: resumeURL,
        designation: data?.designation,
        batch_id: +data?.batch,
        ug_qualification: data?.ugQualification,
        pg_qualification: data?.pgQualification,
        skills: data?.skills?.map((skill) => {
          const proficiency = proficiencyOptions.find(
            (option) =>
              option.proficiency_id === skill.proficiency_id
          );

          return {
            skill_id: skill.skill_id,
            proficiency: proficiency?.proficiency_name || "",
            years_of_experience: skill.relevant_experience,
          };
        }),
      }

      const updatePayload = organizationId === "2" ? updateCandidateCorporatePayload : updateCandidateInstitutePayload

      if (StudentId !== null && StudentId !== undefined) {
        const res = await api.post("UpdateStudentByID", updatePayload);
        if (res?.data?.status === "success") {
          showSnackbar("success", res?.data?.message);
          navigate("/students");
        } else {
          showSnackbar("error", res?.data?.message);
        }
      } else {
        const res = await api.post("AddStudent", createPayload);
        if (res?.data?.status === "success") {
          showSnackbar("success", res?.data?.message);
          navigate("/students");
        } else {
          showSnackbar("error", res?.data?.message);
        }
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      showSnackbar("error", "An error occurred during submission");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", p: 1 }}>
      {overAllLoading ? (
        <OverAllLoader message="Saving student..." />
      ) : (
        <Box>
          {/* Back Button */}
          <Grid
            item
            sx={{
              display: "flex",
              alignItems: "center",
              color: "#1c97e3",
              gap: "4px",
              cursor: "pointer",
              width: "40px",
              mb: 2,
            }}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                window.history.back();
              }
            }}
            onClick={() => window.history.back()}
          >
            <ArrowBackIcon sx={{ fontSize: "17px" }} />
            <Typography
              sx={{ fontWeight: 600, fontSize: "14px", color: "#1c97e3" }}
            >
              Back
            </Typography>
          </Grid>

          {/* Form Card */}
          <Box>
            {/* Header */}
            <Grid
              sx={{
                pb: 2,
                display: "flex",
                gap: 1,
                alignItems: "center",
                borderBottom: "1px solid #d1d1d1",
              }}
            >
              <Grid
                sx={{
                  backgroundColor: "#1c97e3",
                  p: 0.5,
                  borderRadius: "10px",
                }}
              >
                <GroupIcon sx={{ color: "white", fontSize: "35px" }} />
              </Grid>
              <Grid>
                <Typography sx={{ fontSize: "30px", fontWeight: "bold" }}>
                  {addStudent === "true" ? "Add" : "Update"} {getLabel("STUDENT")}
                </Typography>
              </Grid>
            </Grid>

            {/* Form */}
            <form onSubmit={handleSubmit(onSubmit)} noValidate>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  rowGap: 2,
                  p: 3,
                }}
              >
                {/* Basic Info */}
                <Grid
                  sx={{
                    display: "flex",
                    gap: 1,
                    alignItems: "center",
                    mb: 1,
                  }}
                >
                  <Grid>
                    <GroupIcon
                      sx={{
                        color: "#1c97e3",
                        fontSize: "30px",
                        fontWeight: "bold",
                      }}
                    />
                  </Grid>
                  <Typography sx={{ fontSize: "20px", fontWeight: "bold" }}>
                    Basic Information
                  </Typography>
                </Grid>

                {organizationId === "2" && (
                  <Box sx={{ mt: 1, mb: 2 }}>
                    <Typography sx={{ mb: 1 }}>
                      Upload Resume
                      <span style={{ color: "red" }}> *</span>
                    </Typography>

                    <Button
                      component="label"
                      variant="outlined"
                      disableRipple
                      fullWidth
                      sx={{
                        minHeight: "120px",
                        borderRadius: "12px",
                        border: "1.5px dashed #b8c4d6",
                        backgroundColor: "#f8fbff",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 0.8,
                        textTransform: "none",
                        color: "#333",
                        "&:hover": {
                          backgroundColor: "#f1f7ff",
                          borderColor: "#1c97e3",
                        },
                      }}
                    >
                      <CloudUploadOutlinedIcon
                        sx={{
                          fontSize: "32px",
                          color: "#1c97e3",
                        }}
                      />

                      <Typography
                        sx={{
                          fontSize: "14px",
                          fontWeight: 600,
                          color: "#26364a",
                        }}
                      >
                        Upload Resume
                      </Typography>

                      <Typography
                        sx={{
                          fontSize: "12px",
                          color: "#8a97a8",
                        }}
                      >
                        Click to choose a document
                      </Typography>

                      <input
                        type="file"
                        hidden
                        accept=".pdf,.doc,.docx"
                        onChange={(e) => {
                          const file = e.target.files?.[0];

                          if (file) {
                            setResumeFile(file);
                            handleResumeUpload(file);
                          }

                          e.target.value = "";
                        }}
                      />
                    </Button>

                    {/* Selected file */}
                    {resumeFile && (
                      <Box sx={{ mt: 1.5 }}>
                        <Box
                          key={`${resumeFile.name}`}
                          sx={{
                            mb: 1,
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            px: 2,
                            py: 1.5,
                            border: "1px solid #e1e7ef",
                            borderRadius: "10px",
                            backgroundColor: "#fafcff",
                          }}
                        >
                          <DescriptionOutlinedIcon
                            sx={{
                              color: "#1c97e3",
                              fontSize: "24px",
                            }}
                          />

                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography
                              sx={{
                                fontSize: "13px",
                                fontWeight: 600,
                                color: "#26364a",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {resumeFile.name || "Resume File"}
                            </Typography>

                            <Typography
                              sx={{
                                fontSize: "11px",
                                color: "#8a97a8",
                                mt: 0.25,
                              }}
                            >
                              {resumeFile.size ? `${(resumeFile.size / 1024 / 1024).toFixed(2)} MB` : ""}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    )}

                    {/* Validation error */}
                    {errors.resumeFile && (
                      <Typography
                        sx={{
                          color: "#d32f2f",
                          fontSize: "12px",
                          mt: 0.75,
                          ml: 1.5,
                        }}
                      >
                        {errors.resumeFile.message}
                      </Typography>
                    )}
                  </Box>
                )}

                <Grid sx={{ display: "flex", gap: 3 }}>
                  <Grid sx={{ width: "100%" }}>
                    <Typography>
                      First Name <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <Controller
                      name="firstName"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          inputRef={firstText}
                          fullWidth
                          error={!!errors.firstName}
                          helperText={errors.firstName?.message}
                          placeholder="First Name"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              "& fieldset": {
                                borderColor: "white",
                              },
                              "&:hover fieldset": {
                                borderColor: "#d1d1d1",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: "#1c97e3",
                              },
                            },
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid sx={{ width: "100%" }}>
                    <Typography>
                      Last Name <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <Controller
                      name="lastName"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          error={!!errors.lastName}
                          helperText={errors.lastName?.message}
                          placeholder="Last Name"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              "& fieldset": {
                                borderColor: "white",
                              },
                              "&:hover fieldset": {
                                borderColor: "#d1d1d1",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: "#1c97e3",
                              },
                            },
                          }}
                        />
                      )}
                    />
                  </Grid>
                </Grid>

                <Grid sx={{ display: "flex", gap: 3 }}>
                  <Grid sx={{ width: "100%" }}>
                    <Typography>
                      Gender <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <Controller
                      name="gender"
                      control={control}
                      rules={{ required: "Gender is required" }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          select
                          fullWidth
                          placeholder="Select Gender"
                          error={!!errors.gender}
                          helperText={errors.gender?.message}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              "& fieldset": {
                                borderColor: "white",
                              },
                              "&:hover fieldset": {
                                borderColor: "#d1d1d1",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: "#1c97e3",
                              },
                            },
                          }}
                        >
                          <MenuItem value="default" disabled>
                            Select Gender
                          </MenuItem>
                          <MenuItem value="Male">Male</MenuItem>
                          <MenuItem value="Female">Female</MenuItem>
                          <MenuItem value="Other">Other</MenuItem>
                        </TextField>
                      )}
                    />
                  </Grid>
                  <Grid sx={{ width: "100%" }}>
                    <Typography>
                      Date of Birth <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <Controller
                      name="dob"
                      control={control}
                      rules={{ required: "Date of Birth is required" }}
                      render={({ field }) => (
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            {...field}
                            format="DD-MM-YYYY"
                            value={
                              field.value
                                ? dayjs.isDayjs(field.value)
                                  ? field.value
                                  : dayjs(field.value)
                                : null
                            }
                            onChange={(newValue) => field.onChange(newValue)}
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                error: !!errors.dob,
                                helperText: errors.dob?.message,
                                placeholder: "DD-MM-YYYY",
                                sx: {
                                  "& .MuiOutlinedInput-root": {
                                    "& fieldset": { borderColor: "white" },
                                    "&:hover fieldset": { borderColor: "#d1d1d1" },
                                    "&.Mui-focused fieldset": { borderColor: "#1c97e3" },
                                  },
                                  "& .MuiPickersOutlinedInput-notchedOutline": { borderColor: "white" },
                                  "&:hover .MuiPickersOutlinedInput-notchedOutline": { borderColor: "#d1d1d1" },
                                  "& .Mui-focused .MuiPickersOutlinedInput-notchedOutline": { borderColor: "#1c97e3" },
                                }
                              },
                            }}
                          />
                        </LocalizationProvider>
                      )}
                    />
                  </Grid>
                </Grid>

                {addStudent !== "true" && (
                  <Grid sx={{ display: "flex", gap: 3 }}>
                    {/* Status */}
                    <Grid sx={{ width: "49%" }}>
                      <Typography>
                        Status <span style={{ color: "red" }}>*</span>
                      </Typography>
                      <Controller
                        name="status"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            select
                            fullWidth
                            error={!!errors.status}
                            helperText={errors.status?.message}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                "& fieldset": {
                                  borderColor: "white",
                                },
                                "&:hover fieldset": {
                                  borderColor: "#d1d1d1",
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor: "#1c97e3",
                                },
                              },
                            }}
                          >
                            {/* Placeholder */}
                            <MenuItem
                              value="default"
                              disabled
                              sx={{ color: "grey" }}
                            >
                              Select Status
                            </MenuItem>

                            {/* Options */}
                            {statuses.map((status) => (
                              <MenuItem key={status} value={status}>
                                {status.charAt(0).toUpperCase() +
                                  status.slice(1)}
                              </MenuItem>
                            ))}
                          </TextField>
                        )}
                      />
                    </Grid>
                  </Grid>
                )}

                {/* Contact Info */}
                <Grid
                  sx={{
                    display: "flex",
                    gap: 1,
                    alignItems: "center",
                    mb: 1,
                  }}
                >
                  <Grid>
                    <MailOutlineIcon
                      sx={{
                        color: "#1c97e3",
                        fontSize: "30px",
                        fontWeight: "bold",
                      }}
                    />
                  </Grid>
                  <Typography sx={{ fontSize: "20px", fontWeight: "bold" }}>
                    Contact Information
                  </Typography>
                </Grid>
                <Grid sx={{ display: "flex", gap: 3 }}>
                  <Grid sx={{ width: "100%" }}>
                    <Typography>
                      Contact Number <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <Controller
                      name="contactNumber"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          type="number"
                          fullWidth
                          placeholder="Contact Number"
                          error={!!errors.contactNumber}
                          helperText={errors.contactNumber?.message}
                          onInput={(e) => {
                            e.target.value = e.target.value.replace(/\D/g, "");
                            field.onChange(e);
                          }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              "& fieldset": {
                                borderColor: "white",
                              },
                              "&:hover fieldset": {
                                borderColor: "#d1d1d1",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: "#1c97e3",
                              },
                              "& input[type=number]": {
                                MozAppearance: "textfield",
                              },
                              "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
                              {
                                WebkitAppearance: "none",
                                margin: 0,
                              },
                            },
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid sx={{ width: "100%" }}>
                    <Typography>
                      Email <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <Controller
                      name="email"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          placeholder="Email"
                          error={!!errors.email}
                          helperText={errors.email?.message}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              "& fieldset": {
                                borderColor: "white",
                              },
                              "&:hover fieldset": {
                                borderColor: "#d1d1d1",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: "#1c97e3",
                              },
                            },
                          }}
                        />
                      )}
                    />
                  </Grid>
                </Grid>

                {/* Only for Organization */}
                {organizationId === '2' && (
                  <>
                    <Grid sx={{ display: "flex", gap: 3 }}>
                      <Grid sx={{ width: "100%" }}>
                        <Typography>
                          Address
                        </Typography>
                        <Controller
                          name="address"
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              fullWidth
                              placeholder="Address"
                              error={!!errors.address}
                              helperText={errors.address?.message}
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  "& fieldset": {
                                    borderColor: "white",
                                  },
                                  "&:hover fieldset": {
                                    borderColor: "#d1d1d1",
                                  },
                                  "&.Mui-focused fieldset": {
                                    borderColor: "#1c97e3",
                                  },
                                },
                              }}
                            />
                          )}
                        />
                      </Grid>

                      <Grid sx={{ width: "100%" }}>
                        <Typography>
                          City
                        </Typography>
                        <Controller
                          name="city"
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              fullWidth
                              placeholder="City"
                              error={!!errors.city}
                              helperText={errors.city?.message}
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  "& fieldset": {
                                    borderColor: "white",
                                  },
                                  "&:hover fieldset": {
                                    borderColor: "#d1d1d1",
                                  },
                                  "&.Mui-focused fieldset": {
                                    borderColor: "#1c97e3",
                                  },
                                },
                              }}
                            />
                          )}
                        />
                      </Grid>
                    </Grid>

                    <Grid sx={{ display: "flex", gap: 3 }}>
                      <Grid sx={{ width: "100%" }}>
                        <Typography>
                          State
                        </Typography>
                        <Controller
                          name="state"
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              fullWidth
                              placeholder="State"
                              error={!!errors.state}
                              helperText={errors.state?.message}
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  "& fieldset": {
                                    borderColor: "white",
                                  },
                                  "&:hover fieldset": {
                                    borderColor: "#d1d1d1",
                                  },
                                  "&.Mui-focused fieldset": {
                                    borderColor: "#1c97e3",
                                  },
                                },
                              }}
                            />
                          )}
                        />
                      </Grid>

                      <Grid sx={{ width: "100%" }}>
                        <Typography>
                          Pincode
                        </Typography>
                        <Controller
                          name="pincode"
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              fullWidth
                              placeholder="Pincode"
                              error={!!errors.pincode}
                              helperText={errors.pincode?.message}
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  "& fieldset": {
                                    borderColor: "white",
                                  },
                                  "&:hover fieldset": {
                                    borderColor: "#d1d1d1",
                                  },
                                  "&.Mui-focused fieldset": {
                                    borderColor: "#1c97e3",
                                  },
                                },
                              }}
                            />
                          )}
                        />
                      </Grid>
                    </Grid>
                  </>
                )}

                {/* Institute Info */}
                <Grid
                  sx={{
                    display: "flex",
                    gap: 1,
                    alignItems: "center",
                    mb: 1,
                  }}
                >
                  <Grid>
                    <BusinessIcon
                      sx={{
                        color: "#1c97e3",
                        fontSize: "30px",
                        fontWeight: "bold",
                      }}
                    />
                  </Grid>
                  <Typography sx={{ fontSize: "20px", fontWeight: "bold" }}>
                    {getLabel("INSTITUTE")} Information
                  </Typography>
                </Grid>
                <Grid sx={{ display: "flex", gap: 3 }}>
                  <Grid sx={{ width: "100%" }}>
                    <Typography>
                      {getLabel("INSTITUTE")} Name <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <Controller
                      name="instituteName"
                      control={control}
                      render={({ field }) => {
                        const currentValue =
                          instituteNames.find(
                            (inst) => inst.institute_id === field.value
                          ) || null;

                        return (
                          <Autocomplete
                            options={instituteNames}
                            getOptionLabel={(option) => option.name}
                            value={currentValue}
                            onChange={(_, value) =>
                              field.onChange(value ? value.institute_id : "")
                            }
                            isOptionEqualToValue={(option, value) =>
                              option.institute_id === value.institute_id
                            }
                            readOnly={
                              userRole === "INSTITUTE_ADMIN" ||
                              userRole === "TRAINER"
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                placeholder={`Select ${getLabel('INSTITUTE')}`}
                                fullWidth
                                error={!!errors.instituteName}
                                helperText={errors.instituteName?.message}
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    "& fieldset": {
                                      borderColor: "white",
                                    },
                                    "&:hover fieldset": {
                                      borderColor: "#d1d1d1",
                                    },
                                    "&.Mui-focused fieldset": {
                                      borderColor: "#1c97e3",
                                    },
                                  },
                                }}
                              />
                            )}
                          />
                        );
                      }}
                    />
                  </Grid>
                  <Grid sx={{ width: "100%" }}>
                    <Typography>
                      {getLabel("BATCHES")} <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <Controller
                      name="batch"
                      control={control}
                      render={({ field }) => {
                        const currentValue =
                          batchOptions.find(
                            (batch) => batch.batch_id === field.value
                          ) || null;

                        return (
                          <Autocomplete
                            options={batchOptions}
                            getOptionLabel={(option) => option?.batch_name || ""}
                            value={currentValue}
                            onChange={(_, value) =>
                              field.onChange(value ? value.batch_id : "")
                            }
                            isOptionEqualToValue={(option, value) =>
                              option.batch_id === value.batch_id
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                placeholder={`Select ${getLabel("BATCHES")}`}
                                fullWidth
                                error={!!errors.batch}
                                helperText={errors.batch?.message}
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    "& fieldset": {
                                      borderColor: "white",
                                    },
                                    "&:hover fieldset": {
                                      borderColor: "#d1d1d1",
                                    },
                                    "&.Mui-focused fieldset": {
                                      borderColor: "#1c97e3",
                                    },
                                  },
                                }}
                              />
                            )}
                          />
                        );
                      }}
                    />
                  </Grid>
                  {userRole === "INSTITUTE_ADMIN" && (
                    <Grid sx={{ width: "100%" }}>
                      <Typography>
                        Assign {getLabel("TRAINER")} <span style={{ color: "red" }}>*</span>
                      </Typography>

                      <Controller
                        name="assignTrainer"
                        control={control}
                        defaultValue={[]}
                        render={({ field }) => (
                          <Autocomplete
                            multiple
                            disableCloseOnSelect
                            options={trainerNames}
                            getOptionLabel={(option) => option.name || ""}
                            value={trainerNames.filter((t) =>
                              field.value?.includes(t.trainer_id)
                            )}
                            onChange={(_, selectedValues) => {
                              const selectedIds = selectedValues.map((v) => v.trainer_id);
                              field.onChange(selectedIds);
                              setTrainerIds(selectedIds);
                            }}
                            isOptionEqualToValue={(option, value) =>
                              option.trainer_id === value.trainer_id
                            }
                            renderOption={(props, option, { selected }) => (
                              <li {...props}>
                                <Checkbox checked={selected} readOnly sx={{ mr: 1 }} />
                                {option.name}
                              </li>
                            )}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                placeholder={`Select ${getLabel("TRAINER")}`}
                                fullWidth
                                error={!!errors.assignTrainer}
                                helperText={errors.assignTrainer?.message}
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    "& fieldset": { borderColor: "white" },
                                    "&:hover fieldset": { borderColor: "#d1d1d1" },
                                    "&.Mui-focused fieldset": { borderColor: "#1c97e3" },
                                  },
                                }}
                              />
                            )}
                          />
                        )}
                      />
                    </Grid>
                  )}
                </Grid>

                {organizationId === "2" && (
                  <Grid sx={{ display: "flex", gap: 3 }}>
                    <Grid sx={{ width: "100%" }}>
                      <Typography>
                        Total Experience <span style={{ color: "red" }}>*</span>
                      </Typography>

                      <Controller
                        name="totalExperience"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            type="text"
                            fullWidth
                            placeholder="Total Experience"
                            error={!!errors.totalExperience}
                            helperText={errors.totalExperience?.message}
                            onInput={(e) => {
                              const value = e.target.value.replace(/\D/g, "");
                              e.target.value = value;
                              field.onChange(value);
                            }}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                "& fieldset": {
                                  borderColor: "white",
                                },
                                "&:hover fieldset": {
                                  borderColor: "#d1d1d1",
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor: "#1c97e3",
                                },
                              },
                            }}
                          />
                        )}
                      />
                    </Grid>

                    <Grid sx={{ width: "100%" }}>
                      <Typography>
                        Designation <span style={{ color: "red" }}>*</span>
                      </Typography>

                      <Controller
                        name="designation"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            type="text"
                            fullWidth
                            placeholder="Designation"
                            error={!!errors.designation}
                            helperText={errors.designation?.message}
                            onChange={(e) => {
                              field.onChange(e.target.value);
                            }}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                "& fieldset": {
                                  borderColor: "white",
                                },
                                "&:hover fieldset": {
                                  borderColor: "#d1d1d1",
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor: "#1c97e3",
                                },
                              },
                            }}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                )}

                {/* Skills Info */}
                {organizationId === "2" && (
                  <>
                    <Grid
                      sx={{
                        display: "flex",
                        gap: 1,
                        alignItems: "center",
                        mb: 1,
                      }}
                    >
                      <Grid>
                        <BrainCircuitIcon
                          style={{
                            color: "#1c97e3",
                            height: "27px",
                            width: "27px",
                            fontWeight: "bold",
                          }}
                        />
                      </Grid>
                      <Typography sx={{ fontSize: "20px", fontWeight: "bold" }}>
                        {`${getLabel('SUBJECT')}`}
                      </Typography>
                    </Grid>
                    <Grid sx={{ display: "flex", gap: 3 }}>
                      <Controller
                        name="skills"
                        control={control}
                        render={({ field }) => (
                          <TableContainer
                            component={Paper}
                          >
                            <Table>
                              <TableHead>
                                <TableRow className="tableHeader">
                                  <TableCell
                                    className="tableHeader"
                                    align="center"
                                    sx={{ width: "80px", py: 1 }}
                                  >
                                    <b>Select</b>
                                  </TableCell>

                                  <TableCell sx={{ py: 1 }} className="tableHeader">
                                    <b>Skill</b>
                                  </TableCell>

                                  <TableCell
                                    className="tableHeader"
                                    sx={{ width: "250px", py: 1 }}
                                  >
                                    <b>Proficiency</b>
                                  </TableCell>

                                  <TableCell
                                    className="tableHeader"
                                    sx={{ width: "250px", py: 1 }}
                                  >
                                    <b>Relevant Experience</b>
                                  </TableCell>
                                </TableRow>
                              </TableHead>

                              <TableBody className="tablebody">
                                {skills.map((skill, index) => {

                                  const selectedSkillIndex = (field.value || []).findIndex(
                                    (item) => item.skill_id === skill.skill_id
                                  );

                                  const selectedSkill =
                                    selectedSkillIndex !== -1
                                      ? field.value[selectedSkillIndex]
                                      : null;

                                  const isSelected = selectedSkillIndex !== -1;

                                  const proficiencyError =
                                    selectedSkillIndex !== -1
                                      ? errors.skills?.[selectedSkillIndex]?.proficiency_id?.message
                                      : undefined;

                                  const relevantExperienceError =
                                    selectedSkillIndex !== -1
                                      ? errors.skills?.[selectedSkillIndex]?.relevant_experience?.message
                                      : undefined;

                                  return (
                                    <TableRow
                                      key={skill.skill_id}
                                      hover
                                      sx={{
                                        "& .MuiTableCell-root": {
                                          padding: "4px 8px !important", // Reduced padding
                                          borderBottom: "1px solid #eeeeee",
                                          fontSize: "13px", // Smaller font
                                        },
                                        backgroundColor:
                                          index % 2 === 1
                                            ? "#FAFAFA"
                                            : "inherit",
                                        "&:hover": {
                                          backgroundColor: "#f1f1f1",
                                        },
                                        height: "40px",
                                      }}
                                    >
                                      {/* Select */}
                                      <TableCell align="center">
                                        <Checkbox
                                          size="small"
                                          checked={isSelected}
                                          onChange={(e) => {
                                            if (e.target.checked) {
                                              field.onChange([
                                                ...(field.value || []),
                                                {
                                                  skill_id: skill.skill_id,
                                                  proficiency_id: null,
                                                  relevant_experience: "",
                                                },
                                              ]);
                                            } else {
                                              field.onChange(
                                                (field.value || []).filter(
                                                  (item) =>
                                                    item.skill_id !==
                                                    skill.skill_id
                                                )
                                              );
                                            }
                                          }}
                                        />
                                      </TableCell>

                                      {/* Skill */}
                                      <TableCell>
                                        <Typography
                                          sx={{
                                            fontSize: "14px",
                                            fontWeight: 500,
                                          }}
                                        >
                                          {skill.name}
                                        </Typography>
                                      </TableCell>

                                      {/* Proficiency */}
                                      <TableCell>
                                        <TextField
                                          select
                                          fullWidth
                                          size="small"
                                          disabled={!isSelected}
                                          value={
                                            selectedSkill?.proficiency_id ?? ""
                                          }
                                          error={!!proficiencyError}
                                          helperText={proficiencyError || undefined}
                                          onChange={(e) => {
                                            const updatedSkills = [
                                              ...(field.value || []),
                                            ];

                                            updatedSkills[selectedSkillIndex] = {
                                              ...updatedSkills[
                                              selectedSkillIndex
                                              ],
                                              proficiency_id: Number(
                                                e.target.value
                                              ),
                                            };

                                            field.onChange(updatedSkills);
                                          }}
                                          SelectProps={{
                                            displayEmpty: true,
                                            renderValue: (value) => {
                                              if (!value) {
                                                return (
                                                  <span style={{ color: "#999", fontSize: '13px' }}>
                                                    Select Proficiency
                                                  </span>
                                                );
                                              }

                                              const selectedOption = proficiencyOptions.find(
                                                (option) =>
                                                  option.proficiency_id === Number(value)
                                              );

                                              return selectedOption?.proficiency_name || "";
                                            },
                                          }}
                                          sx={{
                                            "& .MuiOutlinedInput-root": {
                                              height: "34px",
                                              fontSize: '14px',

                                              "& fieldset": {
                                                borderColor: "white",
                                              },
                                              "&:hover fieldset": {
                                                borderColor: "#d1d1d1",
                                              },
                                              "&.Mui-focused fieldset": {
                                                borderColor: "#1c97e3",
                                              },
                                            },
                                          }}
                                        >
                                          <MenuItem value="" disabled>
                                            Select Proficiency
                                          </MenuItem>

                                          {proficiencyOptions.map((option) => (
                                            <MenuItem
                                              sx={{ fontSize: '14px' }}
                                              key={option.proficiency_id}
                                              value={option.proficiency_id}
                                            >
                                              {option.proficiency_name}
                                            </MenuItem>
                                          ))}
                                        </TextField>
                                      </TableCell>

                                      {/* Relevant Experience */}
                                      <TableCell>
                                        <TextField
                                          fullWidth
                                          size="small"
                                          type="number"
                                          disabled={!isSelected}
                                          placeholder="Enter years"
                                          value={
                                            selectedSkill?.relevant_experience ??
                                            ""
                                          }
                                          error={!!relevantExperienceError}
                                          helperText={relevantExperienceError || undefined}
                                          onChange={(e) => {
                                            const updatedSkills = [
                                              ...(field.value || []),
                                            ];

                                            updatedSkills[selectedSkillIndex] = {
                                              ...updatedSkills[
                                              selectedSkillIndex
                                              ],
                                              relevant_experience:
                                                e.target.value,
                                            };

                                            field.onChange(updatedSkills);
                                          }}
                                          sx={{
                                            "& .MuiOutlinedInput-root": {
                                              height: "34px",
                                              fontSize: '14px',

                                              "& fieldset": {
                                                borderColor: "white",
                                              },
                                              "&:hover fieldset": {
                                                borderColor: "#d1d1d1",
                                              },
                                              "&.Mui-focused fieldset": {
                                                borderColor: "#1c97e3",
                                              },

                                              "& .MuiInputBase-input::placeholder": {
                                                fontSize: "13px",
                                                opacity: 1,
                                              },
                                              fontSize: '14px'
                                            },
                                          }}
                                          inputProps={{
                                            min: 0,
                                          }}
                                        />
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}

                                {/* No skills */}
                                {skills?.length === 0 && (
                                  <TableRow>
                                    <TableCell
                                      colSpan={4}
                                      align="center"
                                    >
                                      <Typography
                                        sx={{
                                          py: 2,
                                          color: "#888",
                                        }}
                                      >
                                        No skills available
                                      </Typography>
                                    </TableCell>
                                  </TableRow>
                                )}
                              </TableBody>
                            </Table>
                            {typeof errors.skills?.message === "string" && (
                              <Typography
                                sx={{
                                  color: "#d32f2f",
                                  fontSize: "12px",
                                  mt: 0.5,
                                  ml: 2,
                                  mb: 1,
                                }}
                              >
                                {errors.skills.message}
                              </Typography>
                            )}
                          </TableContainer>
                        )}
                      />
                    </Grid>
                  </>
                )}

                {/* Buttons */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 2,
                    mt: 3,
                  }}
                >
                  <Button
                    variant="outlined"
                    color="secondary"
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        window.history.back();
                      }
                    }}
                    onClick={() => window.history.back()}
                    sx={{ textTransform: "none", borderRadius: "12px", px: 3 }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={loading}
                    sx={{
                      textTransform: "none",
                      borderRadius: "12px",
                      px: 4,
                      fontWeight: 600,
                    }}
                  >
                    {addStudent === "true" ? `Add ${getLabel("STUDENT")}` : `Update ${getLabel("STUDENT")}`}
                  </Button>
                </Box>
              </Box>
            </form>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default AddStudent;
